import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1InitialQuestions, step1Part2InitialQuestions, ver01142020, ver11172021, ver11162022,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function ArkansasForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const step1Form = useRef();
  const step1QuestionsWithAnswers = getInitialQuestions(step1InitialQuestions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1QuestionsWithAnswers);

  const step1Part2Form = useRef();
  const step1Part2QuestionsWithAnswers = getInitialQuestions(step1Part2InitialQuestions);
  const [stepOnePartTwoQuestions, setStepOnePartTwoQuestions] = useState(step1Part2QuestionsWithAnswers);

  const [qualifyForLowIncomeRates, setQualifyForLowIncomeRates] = useState(data.qualify_for_low_income_tax_rates || false);

  const getTextByVersion = (q, num) => {
    switch (version) {
      case '01_14_2020':
        return ver01142020[q][num];
      case '11_17_2021':
        return ver11172021[q][num];
      default:
        return ver11162022[q][num];
    }
  };

  // to calculate total number of exemptions
  const calculateExemptions = () => {
    let value = 0;
    const choice = stepOnePartTwoQuestions[0].answer.answer.claim_options;
    switch (choice) {
      case 'claim_yourself':
        value += 1;
        break;
      case 'claim_yourself_and_spouse':
      case 'claim_head_of_household':
        value += 2;
        break;
      default:
        break;
    }
    const dep = stepOnePartTwoQuestions[1].answer.answer.dependents;
    if (/^\d+$/.test(dep)) value += parseInt(dep);

    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of exemptions:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateExemptions()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={stepOnePartTwoQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOnePartTwoQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOnePartTwoQuestions(questions);
        }}
        ref={step1Part2Form}
      />
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            I qualify for low income tax rates.
          </S.QuestionLabel>
        </Flex>
        <Flex>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="checkbox"
                value={qualifyForLowIncomeRates}
                onChange={(e) => setQualifyForLowIncomeRates(e.target.checked)}
                checked={qualifyForLowIncomeRates}
                disabled={isReviewStep}
              />
              <Box ml={2} />
              Yes.
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
      {renderLowIncomeTaxRatesInfo()}
    </div>
  );

  const renderLowIncomeTaxRatesInfo = () => (
    <Flex column mb="20px">
      <Flex column mb="10px">
        <Text>
          You qualify for the low-income tax rates if your total income from all sources is:
        </Text>
      </Flex>
      <Flex column mb="10px">
        <Text bold>
          (a) Single
        </Text>
        <Text>
          { getTextByVersion('lowIncome', 0) }
        </Text>
      </Flex>
      <Flex column mb="10px">
        <Text bold>
          (b) Married filing jointly (1 or less dependents)
        </Text>
        <Text>
          { getTextByVersion('lowIncome', 1) }
        </Text>
      </Flex>
      <Flex column mb="10px">
        <Text bold>
          (c) Married filing jointly (2 or more dependents)
        </Text>
        <Text>
          { getTextByVersion('lowIncome', 2) }
        </Text>
      </Flex>
      <Flex column mb="10px">
        <Text bold>
          (d) Head of household / Qualifying Widow(er) (1 or less dependents)
        </Text>
        <Text>
          { getTextByVersion('lowIncome', 3) }
        </Text>
      </Flex>
      <Flex column mb="10px">
        <Text bold>
          (e) Head of household / Qualifying Widow(er) (2 or more dependents)
        </Text>
        <Text>
          { getTextByVersion('lowIncome', 4) }
        </Text>
      </Flex>
    </Flex>
  );
  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (step1Part2Form && step1Part2Form.current) {
              step1Part2Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const stepOnePartTwoData = stepOnePartTwoQuestions.reduce((acc, question) => {
              let claimYourselfTemp = false;
              let claimYourselfAndSpouseTemp = false;
              let claimHeadOfHouseholdTemp = false;
              if (question.id === 'claim_options') {
                switch (question.answer.answer.claim_options) {
                  case 'claim_yourself':
                    claimYourselfTemp = true;
                    break;
                  case 'claim_yourself_and_spouse':
                    claimYourselfAndSpouseTemp = true;
                    break;
                  case 'claim_head_of_household':
                    claimHeadOfHouseholdTemp = true;
                    break;
                  default:
                    break;
                }
                const claimData = { claim_yourself: claimYourselfTemp, claim_yourself_and_spouse: claimYourselfAndSpouseTemp, claim_head_of_household: claimHeadOfHouseholdTemp };
                return { ...acc, ...claimData };
              }
              return { ...acc, ...question.answer.answer };
            }, {});
            const qualifyForLowIncomeRatesData = { qualify_for_low_income_tax_rates: qualifyForLowIncomeRates };
            const exemptionsData = { total_number_of_exemptions: calculateExemptions().toString() };
            const data = {
              ...stepOneData, ...stepOnePartTwoData, ...qualifyForLowIncomeRatesData, ...exemptionsData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;
  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default ArkansasForm;
